import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import type { LatLngTuple } from 'leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { TextField, Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

// Fix for default marker icon in Leaflet
delete (L.Icon.Default.prototype as unknown as { _getIconUrl?: unknown })
  ._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
});

interface Location {
  latitude: number;
  longitude: number;
  address: string;
  placeId?: string; // Nominatim doesn't have a direct 'placeId' like Google, but we can use OSM ID or similar if needed
}

interface OSMLocationPickerProps {
  label: string;
  value?: Location;
  onChange: (location?: Location) => void;
  error?: boolean;
  helperText?: string;
  initialAddress?: string;
}

const OSMLocationPicker: React.FC<OSMLocationPickerProps> = ({
  label,
  value,
  onChange,
  error,
  helperText,
  initialAddress,
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState(
    initialAddress || value?.address || ''
  );
  const [markerPosition, setMarkerPosition] = useState<LatLngTuple | null>(
    value ? [value.latitude, value.longitude] : null
  );
  const mapRef = useRef<L.Map | null>(null);

  useEffect(() => {
    // Update marker position when value prop changes
    if (
      value &&
      (!markerPosition ||
        value.latitude !== markerPosition[0] ||
        value.longitude !== markerPosition[1])
    ) {
      setMarkerPosition([value.latitude, value.longitude]);
    }
  }, [value]);

  useEffect(() => {
    // Set map view when marker position changes and map is ready
    if (mapRef.current && markerPosition) {
      mapRef.current.setView(markerPosition, 11);
    }
  }, [markerPosition]);

  const handleSearch = async (query: string) => {
    if (!query) return;
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=1`
      );
      const data = await response.json();
      if (data && data.length > 0) {
        const { lat, lon, display_name, osm_id } = data[0];
        const newLocation: Location = {
          latitude: parseFloat(lat),
          longitude: parseFloat(lon),
          address: display_name,
          placeId: osm_id ? String(osm_id) : undefined,
        };
        setMarkerPosition([newLocation.latitude, newLocation.longitude]);
        onChange(newLocation);
        // Map view will be updated automatically by the useEffect when markerPosition changes
      } else {
        onChange(undefined); // Clear location if no results
      }
    } catch (err) {
      console.error('Error geocoding:', err);
      onChange(undefined);
    }
  };

  const handleMapClick = async (e: L.LeafletMouseEvent) => {
    const { lat, lng } = e.latlng;
    setMarkerPosition([lat, lng]);

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`
      );
      const data = await response.json();
      if (data && data.display_name) {
        const newLocation: Location = {
          latitude: lat,
          longitude: lng,
          address: data.display_name,
          placeId: data.osm_id ? String(data.osm_id) : undefined,
        };
        setSearchQuery(data.display_name);
        onChange(newLocation);
        // Map view will be updated automatically by the useEffect when markerPosition changes
      } else {
        onChange(undefined);
      }
    } catch (err) {
      console.error('Error reverse geocoding:', err);
      onChange(undefined);
    }
  };

  const MapEvents = () => {
    const map = useMapEvents({
      click: handleMapClick,
    });

    // Use effect to handle map setup after it's available
    React.useEffect(() => {
      if (map && !mapRef.current) {
        mapRef.current = map;

        // Set initial view if we have a marker position
        if (markerPosition) {
          map.setView(markerPosition, 11);
        } else {
          map.setView([0, 0], 2);
        }
      }
    }, [map, markerPosition]);

    return null;
  };

  return (
    <Box sx={{ width: '100%' }}>
      <TextField
        label={label}
        fullWidth
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        onKeyPress={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            handleSearch(searchQuery);
          }
        }}
        margin="normal"
        error={error}
        helperText={helperText}
      />
      <Box
        sx={{
          height: 400,
          width: '100%',
          mt: 2,
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        <MapContainer
          center={[0, 0]}
          zoom={2}
          scrollWheelZoom={true}
          style={{ height: '100%', width: '100%' }}
        >
          <MapEvents />
          <TileLayer
            attribution='© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {markerPosition && <Marker position={markerPosition} />}
        </MapContainer>
      </Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mt: 1, display: 'block' }}
      >
        {t('common.osmAttribution', 'Map data © OpenStreetMap contributors')}
      </Typography>
    </Box>
  );
};

export default OSMLocationPicker;
